import type { IconKind } from '../components/Icon/Icon';
import type { Appointment } from './types';

/**
 * Insurance-estimate data seam for the appointment modal (AppointmentDetail).
 *
 * ⚠️ TEMPORARY MOCK — the Opportunities + Insurance Estimate + Family Overview data will be
 * served by the Insurance module (WIP). Everything insurance-related below is placeholder data
 * shaped to match the final contract, isolated here so wiring the real module is a single-spot
 * swap: replace the body of `getAppointmentInsurance` with a fetch/selector and keep the types.
 */

export type OpportunityPriority = 'High' | 'Medium' | 'Low';

/** Colour intent for an opportunity's headline estimate figure. */
export type EstimateTone = 'ai' | 'gold' | 'success' | 'ink';

export interface OpportunityItem {
  id: string;
  icon: IconKind;
  /** Accent colour token for the icon chip, e.g. 'var(--ai-3)'. */
  accent: string;
  title: string;
  priority?: OpportunityPriority;
  /** Primary line, e.g. "Orthodontic benefits available". */
  description: string;
  /** Secondary line, e.g. "$2,000 lifetime benefit remaining". */
  detail?: string;
  /** Label above the figure — "Est. insurance coverage". */
  estLabel?: string;
  /** Coverage as a percentage or amount, e.g. "100%" / "Up to $2,000". */
  estValue?: string;
  /** Optional small note under the figure. */
  estNote?: string;
  estTone?: EstimateTone;
  /** Visually promote this card (teal outline in the mockup). */
  highlighted?: boolean;
}

export interface ProcedureRow {
  code: string;
  description: string;
  officeFee: number;
  /** Amount insurance is expected to cover (positive; rendered as a credit). */
  insuranceEst: number;
  patientEst: number;
}

export interface PlanTiles {
  annualMax: number;
  annualRemaining: number;
  deductible: number;
  deductibleMet: boolean;
  orthoMax: number;
  orthoRemaining: number;
  planType: string;
  /** e.g. "01/01/2025". */
  effectiveFrom: string;
  /** e.g. "12/31/2025". */
  effectiveTo: string;
}

export interface InsuranceEstimate {
  /** Insurance carrier name, e.g. "Delta Dental". */
  carrier: string;
  /** Human phrase for when eligibility was last verified, e.g. "today 8:42 AM". */
  verifiedAt?: string;
  patientToday: number;
  /** Line under the "patient today" figure. */
  patientNote?: string;
  officeFee?: number;
  insuranceEst?: number;
  patientEst?: number;
  procedures: ProcedureRow[];
  plan?: PlanTiles;
}

export type RecallStatus = 'on_schedule' | 'overdue';

export interface FamilyOverviewMember {
  id: string;
  name: string;
  /** "You" | "Spouse" | "Son" … */
  relation: string;
  isSelf?: boolean;
  /** e.g. "Next recall: Jul 28, 2025". */
  recallLabel: string;
  recallStatus: RecallStatus;
  /** e.g. "On schedule" / "Overdue (15 days)". */
  recallNote: string;
  unscheduledTreatment?: number;
  unscheduledItems?: number;
  benefitsRemaining?: number;
  /** Denominator for the benefits-remaining bar. */
  benefitsMax?: number;
}

export interface AppointmentInsuranceDossier {
  opportunities: OpportunityItem[];
  estimate?: InsuranceEstimate;
  family: FamilyOverviewMember[];
}

/**
 * Returns the (mock) insurance dossier for an appointment.
 *
 * The self member's name is taken from the appointment so the panel feels connected; every
 * figure is placeholder until the Insurance module lands. Replace the body here — not the
 * callers or the component — when the real data source is ready.
 */
export function getAppointmentInsurance(appt: Appointment): AppointmentInsuranceDossier {
  const selfName = appt.patient || 'This patient';

  return {
    opportunities: [
      {
        id: 'ortho',
        icon: 'sparkle',
        accent: 'var(--ai-3)',
        title: 'Ortho / Invisalign Opportunity',
        priority: 'High',
        description: 'Orthodontic benefits available',
        detail: '$2,000 lifetime benefit remaining',
        estLabel: 'Est. insurance coverage',
        estValue: 'Up to $2,000',
        estTone: 'ai',
      },
      {
        id: 'fluoride',
        icon: 'shield',
        accent: 'var(--ai-4)',
        title: 'Fluoride Eligible Today',
        priority: 'High',
        description: 'Covered at 100%',
        detail: 'Next eligible: Today',
        estLabel: 'Est. insurance coverage',
        estValue: '100%',
        estTone: 'success',
      },
      {
        id: 'sealant',
        icon: 'shield-check',
        accent: 'var(--gold)',
        title: 'Sealant Benefit Available',
        priority: 'Medium',
        description: 'Sealants covered for dependents',
        detail: 'Age requirement: under 16',
        estLabel: 'Est. insurance coverage',
        estValue: '100%',
        estTone: 'gold',
      },
      {
        id: 'unused-benefits',
        icon: 'coins',
        accent: 'var(--success)',
        title: 'Unused Benefits + Unscheduled Treatment',
        priority: 'High',
        description: 'You have $1,340 in annual benefits remaining',
        detail: 'Total unscheduled treatment: $2,770',
        estLabel: 'Est. insurance coverage',
        estValue: 'Up to $1,340',
        estTone: 'success',
        highlighted: true,
      },
      {
        id: 'benefits-reset',
        icon: 'calendar',
        accent: 'var(--gold)',
        title: 'Benefits Reset Dec 31',
        priority: 'Medium',
        description: '$1,340 in benefits remaining',
        detail: 'Plan resets in 112 days',
        estLabel: 'Est. insurance coverage',
        estValue: 'Up to $1,340',
        estTone: 'success',
      },
    ],
    estimate: {
      carrier: 'Delta Dental',
      verifiedAt: 'today 8:42 AM',
      patientToday: 275,
      officeFee: 981,
      insuranceEst: 706,
      patientEst: 275,
      procedures: [
        {
          code: 'D2740',
          description: 'Crown – Porcelain/Ceramic',
          officeFee: 850,
          insuranceEst: 605,
          patientEst: 245,
        },
        {
          code: 'D0220',
          description: 'Intraoral – Periapical 1st Film',
          officeFee: 31,
          insuranceEst: 31,
          patientEst: 0,
        },
        {
          code: 'D2950',
          description: 'Core Buildup, Including Any Pins',
          officeFee: 100,
          insuranceEst: 70,
          patientEst: 30,
        },
      ],
      plan: {
        annualMax: 2000,
        annualRemaining: 1340,
        deductible: 50,
        deductibleMet: true,
        orthoMax: 2000,
        orthoRemaining: 2000,
        planType: 'PPO',
        effectiveFrom: '01/01/2025',
        effectiveTo: '12/31/2025',
      },
    },
    family: [
      {
        id: 'self',
        name: selfName,
        relation: 'You',
        isSelf: true,
        recallLabel: 'Next recall: Jul 28, 2025',
        recallStatus: 'on_schedule',
        recallNote: 'On schedule',
        unscheduledTreatment: 2770,
        unscheduledItems: 3,
        benefitsRemaining: 1340,
        benefitsMax: 2000,
      },
      {
        id: 'spouse',
        name: 'Angela Love',
        relation: 'Spouse',
        recallLabel: 'Next recall: May 14, 2025',
        recallStatus: 'overdue',
        recallNote: 'Overdue (15 days)',
        unscheduledTreatment: 1250,
        unscheduledItems: 2,
        benefitsRemaining: 1120,
        benefitsMax: 2000,
      },
      {
        id: 'son',
        name: 'Jordan Love',
        relation: 'Son',
        recallLabel: 'Next recall: Oct 2, 2025',
        recallStatus: 'on_schedule',
        recallNote: 'On schedule',
        unscheduledTreatment: 0,
        unscheduledItems: 0,
        benefitsRemaining: 1000,
        benefitsMax: 2000,
      },
    ],
  };
}
